/**
 * A máscara de telefone do CRM (13.2, chamado de 21/10/2026, P7; FR-26 emendado) — **uma** regra,
 * só no SPA.
 *
 * Ela vale para todo `input type="tel"` enquanto se digita (`mascararAoDigitar`, ligada ao campo por
 * `aoDigitarTelefone`), para o que se grava (`telefoneParaGravar`) e para a única exibição
 * somente-leitura de telefone do cadastro (`formatarTelefone`, na ficha do Negócio; a conversa tem
 * a grafia do WhatsApp, `formatarNumeroDeWhatsapp`, fora daqui). O servidor **não**
 * formata nem valida: o `cadastro` continua aceitando texto livre, e o FR-26 diz "nunca recusa".
 *
 * **A revisão Alembic da 13.2 tem uma cópia congelada de `formatarTelefone`**
 * (`crm-backend/servicos/cadastro/migrations/versions/a6c2e9f41d35_telefone_formatado.py`), que
 * reformatou os telefones já gravados. As duas estão presas pelos mesmos vetores
 * (`crm-backend/servicos/cadastro/tests/vetores_da_mascara_de_telefone.json`, lidos aqui por
 * `?raw`): mudar a regra aqui sem olhar a cópia reprova a suíte.
 *
 * **A regra.** Toda função recebe o texto sem as marcas invisíveis (categoria `Cf`: U+200B, U+202A,
 * U+FEFF…) e sem os espaços das pontas (espaço é a categoria `Zs` mais tab, `\n` e `\r`).
 *
 * - **Livre** é o texto que tem um caractere que não é dígito ASCII, espaço, `(`, `)`, `.` nem traço
 *   (categoria `Pd`) — o `+`, a letra do ramal, `#`, `/`, `*`, o U+2212, o dígito de outra escrita —,
 *   ou cujo primeiro dígito ASCII é `0` (0800, `(022)`). O livre fica como está.
 * - **Formatável** é o não livre com 10 dígitos — `(DD) XXXX-XXXX` — ou com 11 e `9` no terceiro —
 *   `(DD) 9XXXX-XXXX`. Os 11 sem o nono (`14155550100`) **não** viram celular (D-8). Com mais de um
 *   grupo de dígitos, o primeiro tem de ter exatamente 2, o DDD (D-13), e o último, ao menos 4
 *   (D-17).
 * - Todo o resto fica **idêntico** na exibição.
 *
 * **A chave de casamento não muda** (`chave-do-telefone.ts`): a regra só tira o que não é dígito e
 * põe `(`, `)`, espaço e `-`, e a chave é derivada só dos dígitos ASCII.
 *
 * **Os tamanhos e os dígitos vêm da chave**, e não são redigitados: `DIGITOS`,
 * `TAMANHO_BRASILEIRO_SEM_O_NONO`, `TAMANHO_BRASILEIRO_COM_O_NONO` e `INDICE_DO_NONO`.
 */

import type { ChangeEvent } from 'react'

import {
  DIGITOS,
  INDICE_DO_NONO,
  TAMANHO_BRASILEIRO_COM_O_NONO,
  TAMANHO_BRASILEIRO_SEM_O_NONO,
  digitosDe,
} from './chave-do-telefone'

/** As marcas invisíveis que o texto colado traz (U+202A/U+202C do WhatsApp, U+200B, U+FEFF…). */
const MARCA_INVISIVEL = /\p{Cf}/u
/** Espaço: a categoria `Zs` (U+0020, U+00A0, U+202F, U+2007…) mais tab, `\n` e `\r`. */
const ESPACO = /[\p{Zs}\t\n\r]/u
/** O traço: a categoria `Pd` (o `-` ASCII, U+2010, U+2011, U+2013, U+2014…). */
const TRACO = /\p{Pd}/u
/** A pontuação que a máscara aceita sem soltar o campo. */
const PONTUACAO_ACEITA = '().'
/** O que, digitado logo depois de um número completo, solta o resto (o ramal, o "- 21"). */
const SEPARADORES = ' -.('

/** O texto sem as marcas `Cf`. */
function semMarcas(texto: string): string {
  let limpo = ''
  for (const caractere of texto) {
    if (!MARCA_INVISIVEL.test(caractere)) {
      limpo += caractere
    }
  }
  return limpo
}

/** O texto sem as marcas `Cf` e sem os espaços das pontas — a entrada de toda função da regra. */
function limpar(texto: string): string {
  const caracteres = [...semMarcas(texto)]
  let inicio = 0
  let fim = caracteres.length
  while (inicio < fim && ESPACO.test(caracteres[inicio] ?? '')) {
    inicio += 1
  }
  while (fim > inicio && ESPACO.test(caracteres[fim - 1] ?? '')) {
    fim -= 1
  }
  return caracteres.slice(inicio, fim).join('')
}

function ehAceito(caractere: string): boolean {
  return (
    DIGITOS.includes(caractere) ||
    ESPACO.test(caractere) ||
    PONTUACAO_ACEITA.includes(caractere) ||
    TRACO.test(caractere)
  )
}

/**
 * Se o texto é **livre**: tem `+`, letra, símbolo ou dígito de outra escrita, ou começa pelo dígito
 * `0`. O livre nunca ganha máscara — nem ao digitar, nem ao exibir, nem na migração.
 */
export function ehTelefoneLivre(texto: string): boolean {
  const limpo = limpar(texto)
  for (const caractere of limpo) {
    if (!ehAceito(caractere)) {
      return true
    }
  }
  return digitosDe(limpo).startsWith('0')
}

/** A grafia da máscara para os dígitos de um número completo, ou `null` quando não é formatável. */
function grafiaCompleta(digitos: string): string | null {
  if (digitos.length === TAMANHO_BRASILEIRO_SEM_O_NONO) {
    return `(${digitos.slice(0, 2)}) ${digitos.slice(2, 6)}-${digitos.slice(6)}`
  }
  if (digitos.length === TAMANHO_BRASILEIRO_COM_O_NONO && digitos[INDICE_DO_NONO] === '9') {
    return `(${digitos.slice(0, 2)}) ${digitos.slice(2, 7)}-${digitos.slice(7)}`
  }
  return null
}

/**
 * A máscara progressiva de até 10 dígitos: `(2`, `(22`, `(22) 9`, `(22) 9975-5`, `(22) 9975-5099`.
 */
function grafiaProgressiva(digitos: string): string {
  if (digitos.length <= 2) {
    return `(${digitos}`
  }
  const ddd = `(${digitos.slice(0, 2)}) `
  if (digitos.length <= 6) {
    return ddd + digitos.slice(2)
  }
  return `${ddd}${digitos.slice(2, 6)}-${digitos.slice(6)}`
}

/**
 * D-13: com mais de um grupo de dígitos (corridas separadas por espaço, traço, ponto ou parêntese),
 * o **primeiro** precisa ter exatamente 2 dígitos, o DDD. `3333-4444 21`, `9975-5099 22` e
 * `229975-5099` não são um número com DDD na frente, e formatá-los inventaria um DDD; `(22)9975.5099`
 * e `22 9 9755-0997` continuam formatáveis.
 */
function primeiroGrupoNaoEhDdd(texto: string): boolean {
  const grupos = texto.match(/[0-9]+/g) ?? []
  return grupos.length > 1 && grupos[0]?.length !== 2
}

/** O menor último grupo de um número com mais de um grupo (D-17): o fim do assinante. */
const MINIMO_DO_ULTIMO_GRUPO = 4

/**
 * Se os grupos de dígitos **não formam** um número com a máscara: o D-13 (o primeiro não é o DDD)
 * ou o D-17 — com mais de um grupo, o **último** tem menos de 4 dígitos. `(22) 9975-5099 7` e
 * `22 9975 509 9` não são um número escrito com separadores, e formatá-los inventaria outro
 * número (o 7 viraria o 11º dígito, o celular); `22 9 9755 0997` continua formatável.
 */
function gruposNaoFormamNumero(texto: string): boolean {
  const grupos = texto.match(/[0-9]+/g) ?? []
  if (grupos.length <= 1) {
    return false
  }
  return primeiroGrupoNaoEhDdd(texto) || (grupos.at(-1)?.length ?? 0) < MINIMO_DO_ULTIMO_GRUPO
}

/**
 * O telefone como a tela o exibe: o formatável na grafia da máscara; **todo o resto idêntico**
 * (inclusive o vazio). É a regra que a revisão Alembic da 13.2 congelou.
 */
export function formatarTelefone(texto: string): string {
  const limpo = limpar(texto)
  if (ehTelefoneLivre(limpo) || gruposNaoFormamNumero(limpo)) {
    return texto
  }
  return grafiaCompleta(digitosDe(limpo)) ?? texto
}

/**
 * A posição logo depois do `n`-ésimo dígito ASCII do texto (a contagem começa em 1), ou `null`
 * quando o texto não tem tantos dígitos.
 */
function depoisDoDigito(texto: string, n: number): number | null {
  let vistos = 0
  for (let indice = 0; indice < texto.length; indice += 1) {
    if (DIGITOS.includes(texto[indice] ?? '')) {
      vistos += 1
      if (vistos === n) {
        return indice + 1
      }
    }
  }
  return null
}

/**
 * Se o texto (não livre) tem um **separador logo depois de um número completo**: espaço, `-`, `.`
 * ou `(` depois do 10º dígito, ou depois do 11º quando os 11 primeiros são um celular. É o que
 * solta o resto — `(22) 2222-3333 ramal 21`, `(22) 2222-3333 - 21` — sem a máscara engolir o
 * ramal.
 */
function ehSolto(texto: string): boolean {
  const digitos = digitosDe(texto)
  const completos =
    digitos.length >= TAMANHO_BRASILEIRO_COM_O_NONO && digitos[INDICE_DO_NONO] === '9'
      ? [TAMANHO_BRASILEIRO_SEM_O_NONO, TAMANHO_BRASILEIRO_COM_O_NONO]
      : [TAMANHO_BRASILEIRO_SEM_O_NONO]
  return completos.some((tamanho) => {
    const fim = depoisDoDigito(texto, tamanho)
    return fim !== null && fim < texto.length && SEPARADORES.includes(texto[fim] ?? '')
  })
}

/**
 * O telefone como ele vai ao serviço — no `blur` de um campo que mudou e no corpo do envio de todo
 * telefone que difere do gravado:
 *
 * - o livre sai aparado; o número completo seguido de separador, que a digitação soltou
 *   (`(22) 9975-5099 7`, `(22) 2222-3333 - 21`), também — e **antes** da grafia completa, senão o
 *   11º dígito depois do espaço viraria o celular `(22) 99755-0997`;
 * - com o comprimento de um número completo, o que os grupos não formam (o primeiro não é o DDD,
 *   D-13, ou o último tem menos de 4 dígitos, D-17) sai aparado, como está — só os dígitos
 *   mostrariam depois outro número formatado;
 * - o formatável sai formatado;
 * - o não livre fora do formato sai **só com os dígitos**: a máscara parcial `(40) 0412-34` nunca é
 *   gravada como se fosse um número (o D-17 não a segura: ela não tem o comprimento de um número).
 */
export function telefoneParaGravar(texto: string): string {
  const limpo = limpar(texto)
  if (ehTelefoneLivre(limpo) || ehSolto(limpo)) {
    return limpo
  }
  const digitos = digitosDe(limpo)
  const completa = grafiaCompleta(digitos)
  if (completa === null) {
    return digitos
  }
  return gruposNaoFormamNumero(limpo) ? limpo : completa
}

/** O que `mascararAoDigitar` recebe: o valor de antes, o que o navegador pôs no campo e o cursor. */
export interface Digitacao {
  /** O valor do campo antes da tecla (o estado). */
  anterior: string
  /** O valor que o navegador pôs no campo depois da tecla (`input.value`). */
  digitado: string
  /** O `selectionStart` depois da tecla. */
  cursor: number
  /** O `inputType` do `InputEvent` (`insertText`, `deleteContentBackward`…); `''` quando não há. */
  tipoDaEntrada: string
}

/** O que o campo passa a mostrar, e onde fica o cursor. */
export interface Mascarado {
  valor: string
  cursor: number
}

/** Quantos dígitos ASCII há antes da posição. */
function digitosAntes(texto: string, posicao: number): number {
  return digitosDe(texto.slice(0, posicao)).length
}

/** O texto sem o caractere daquela posição. */
function semAPosicao(texto: string, indice: number): string {
  return texto.slice(0, indice) + texto.slice(indice + 1)
}

/**
 * A máscara enquanto se digita — função pura, sem DOM.
 *
 * - `historyUndo`/`historyRedo` passam como vieram.
 * - **Livre**: fica como digitado, só sem as marcas `Cf`.
 * - Número completo seguido de separador: fica como digitado (o resto está solto).
 * - Colado ou autofill com grupos que não formam número (D-13, D-17): como veio.
 * - **Não livre, até 10 dígitos**: a máscara progressiva; **11 com o nono**: a do celular.
 * - **Fora do formato** (11 sem o nono, ou mais de 11): só os dígitos quando a entrada é
 *   `insertText`; colado, autofill ou `insertReplacementText` ficam como vieram.
 * - **Apagar**: o `deleteContentBackward` que apagou um único caractere da máscara apaga o dígito
 *   anterior; o `deleteContentForward`, o seguinte; a seleção que pegou só máscara, ou sem dígito
 *   vizinho, só reformata.
 * - **Cursor**: fica depois do mesmo número de dígitos em que estava.
 *
 * Nunca recusa: o pior que acontece é o texto ficar como veio.
 */
export function mascararAoDigitar({
  anterior,
  digitado,
  cursor,
  tipoDaEntrada,
}: Digitacao): Mascarado {
  if (tipoDaEntrada === 'historyUndo' || tipoDaEntrada === 'historyRedo') {
    return { valor: digitado, cursor }
  }
  let texto = semMarcas(digitado)
  // As marcas `Cf` que saíram antes do cursor puxam o cursor junto.
  let posicao = semMarcas(digitado.slice(0, cursor)).length

  if (ehTelefoneLivre(texto) || ehSolto(texto)) {
    return { valor: texto, cursor: posicao }
  }
  /*
   * D-13 e D-17: colado ou autofill (`insertFromPaste`, `insertReplacementText`, entrada sem tipo)
   * com grupos que não formam número fica como veio. Tecla a tecla (`insertText`) a máscara
   * progressiva continua valendo, e o apagar (`delete…`) segue a matriz própria dele: apagar `) `
   * de `(22) 9975-1099` deixa o DDD grudado no número por um instante, e a máscara o devolve.
   */
  const ehColagem = tipoDaEntrada !== 'insertText' && !tipoDaEntrada.startsWith('delete')
  if (ehColagem && gruposNaoFormamNumero(limpar(texto))) {
    return { valor: texto, cursor: posicao }
  }

  /*
   * Um caractere só da máscara apagado (o `)`, o espaço, o `-`): sem isto, o Backspace depois do
   * traço "não faria nada" — a máscara poria o traço de volta. Só quando o texto de antes era
   * máscara de verdade: no livre e no solto o caractere apagado era da pessoa.
   */
  const apagouUmDaMascara =
    anterior.length === texto.length + 1 &&
    digitosDe(anterior) === digitosDe(texto) &&
    !ehTelefoneLivre(anterior) &&
    !ehSolto(anterior)
  if (apagouUmDaMascara && tipoDaEntrada === 'deleteContentBackward') {
    for (let indice = posicao - 1; indice >= 0; indice -= 1) {
      if (DIGITOS.includes(texto[indice] ?? '')) {
        texto = semAPosicao(texto, indice)
        posicao -= 1
        break
      }
    }
  } else if (apagouUmDaMascara && tipoDaEntrada === 'deleteContentForward') {
    for (let indice = posicao; indice < texto.length; indice += 1) {
      if (DIGITOS.includes(texto[indice] ?? '')) {
        texto = semAPosicao(texto, indice)
        break
      }
    }
  }

  const digitos = digitosDe(texto)
  if (digitos === '') {
    // Sem dígito ainda (o `(` de quem vai digitar `(022)`): nada a mascarar, fica como veio.
    return { valor: texto, cursor: posicao }
  }
  const antes = digitosAntes(texto, posicao)
  const grafia =
    digitos.length <= TAMANHO_BRASILEIRO_SEM_O_NONO
      ? grafiaProgressiva(digitos)
      : grafiaCompleta(digitos)

  if (grafia === null) {
    if (tipoDaEntrada !== 'insertText') {
      return { valor: texto, cursor: posicao }
    }
    return { valor: digitos, cursor: antes }
  }
  const novoCursor = antes === 0 ? 0 : (depoisDoDigito(grafia, antes) ?? grafia.length)
  return { valor: grafia, cursor: novoCursor }
}

/**
 * Liga a máscara a um `input type="tel"` controlado: devolve o valor que o estado deve guardar.
 *
 * **O valor e o cursor são escritos no próprio elemento antes do `setState`** de quem chama. Num
 * `input` controlado, trocar o valor por outro de tamanho diferente joga o cursor para o fim; aqui
 * o elemento já está com o valor novo e a seleção certa quando o React renderiza, ele compara,
 * acha igual e não toca no cursor.
 *
 * Durante uma composição (IME, `isComposing`) o texto passa como veio: o próximo `insertText` ou o
 * `blur` normalizam. Só existe em `type="tel"` — o `type="email"` nem expõe `selectionStart`.
 */
export function aoDigitarTelefone(evento: ChangeEvent<HTMLInputElement>, anterior: string): string {
  const campo = evento.target
  const nativo = evento.nativeEvent as Partial<InputEvent>
  if (nativo.isComposing === true) {
    return campo.value
  }
  const { valor, cursor } = mascararAoDigitar({
    anterior,
    digitado: campo.value,
    cursor: campo.selectionStart ?? campo.value.length,
    tipoDaEntrada: nativo.inputType ?? '',
  })
  if (campo.value !== valor) {
    campo.value = valor
  }
  if (campo.ownerDocument.activeElement === campo) {
    campo.setSelectionRange(cursor, cursor)
  }
  return valor
}
